// Admin-only inbox for support tickets submitted via the SupportWidget.
// Lists tickets with filters (status), allows admin to mark resolved and
// add internal notes.
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { LifeBuoy, RefreshCw, ChevronRight, ExternalLink } from 'lucide-react';

const STATUS_STYLES = {
  open:         'border-amber-800 text-amber-300 bg-amber-950/40',
  in_progress:  'border-cyan-800 text-cyan-300 bg-cyan-950/40',
  resolved:     'border-emerald-800 text-emerald-300 bg-emerald-950/40',
  wont_fix:     'border-slate-700 text-slate-400 bg-slate-800/40',
};

const CATEGORY_STYLES = {
  bug:      'border-red-900 text-red-300 bg-red-950/30',
  question: 'border-cyan-900 text-cyan-300 bg-cyan-950/30',
  feature:  'border-fuchsia-900 text-fuchsia-300 bg-fuchsia-950/30',
  other:    'border-slate-700 text-slate-300 bg-slate-800/30',
};

function TicketRow({ ticket, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(ticket.status);
  const [notes, setNotes] = useState(ticket.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate(ticket.id, { status, admin_notes: notes });
      toast.success('Ticket updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-800 rounded-lg bg-slate-900/40" data-testid={`support-ticket-${ticket.id}`}>
      <button
        className="w-full flex items-center gap-3 p-3 hover:bg-slate-900/70 transition-colors text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <Badge variant="outline" className={CATEGORY_STYLES[ticket.category] || CATEGORY_STYLES.other}>
          {ticket.category}
        </Badge>
        <span className="text-slate-100 font-medium truncate flex-1">{ticket.subject}</span>
        <span className="text-slate-500 text-xs hidden md:inline truncate max-w-[220px]">
          {ticket.user_name || ticket.user_email}
        </span>
        <Badge variant="outline" className={STATUS_STYLES[ticket.status] || STATUS_STYLES.open}>
          {ticket.status.replace(/_/g, ' ')}
        </Badge>
      </button>
      {expanded && (
        <div className="border-t border-slate-800 p-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <div><span className="text-slate-500">From: </span>
              <span className="text-slate-200">{ticket.user_name || '—'} &lt;{ticket.user_email || '—'}&gt;</span>
            </div>
            <div><span className="text-slate-500">Created: </span>
              <span className="text-slate-200">{new Date(ticket.created_at).toLocaleString()}</span>
            </div>
            <div className="md:col-span-2"><span className="text-slate-500">Page: </span>
              {ticket.page_url ? (
                <a href={ticket.page_url} target="_blank" rel="noreferrer"
                   className="text-cyan-300 hover:underline break-all">
                  {ticket.page_url} <ExternalLink className="w-3 h-3 inline-block ml-1" />
                </a>
              ) : <span className="text-slate-400">—</span>}
            </div>
          </div>
          <div>
            <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Message</div>
            <div className="whitespace-pre-wrap text-slate-200 text-sm bg-slate-950 border border-slate-800 rounded p-3">
              {ticket.message}
            </div>
          </div>
          <div className="grid md:grid-cols-[220px_1fr] gap-3 items-start">
            <div>
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Status</div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="ticket-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="wont_fix">Won't Fix</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Internal notes</div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-950 border-slate-800 min-h-[80px]"
                placeholder="Not visible to the reporter."
              />
            </div>
          </div>
          <div className="flex items-center justify-end">
            <Button onClick={save} disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="ticket-save-btn">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminSupportPage() {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const { data } = await api.get('/admin/support/tickets', { params });
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (id, patch) => {
    const { data } = await api.patch(`/admin/support/tickets/${id}`, patch);
    setTickets((prev) => prev.map((t) => (t.id === id ? data : t)));
  };

  const openCount = tickets.filter((t) => t.status === 'open').length;

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-white text-2xl font-semibold flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-cyan-400" /> Support Inbox
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Tickets submitted from the in-app Support widget. {openCount} open.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-slate-950 border-slate-800 w-[180px]"
              data-testid="ticket-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="all">All tickets</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="wont_fix">Won't Fix</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} variant="ghost" disabled={loading}
            className="text-slate-400 hover:text-slate-200"
            data-testid="ticket-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-lg">
            {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {tickets.length === 0 && !loading && (
            <div className="text-slate-500 text-sm py-6 text-center">
              No tickets yet — you'll see them here when users click the floating Support button.
            </div>
          )}
          {tickets.map((t) => (
            <TicketRow key={t.id} ticket={t} onUpdate={handleUpdate} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
